import React, { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { LuPanelLeftOpen, LuPanelRightOpen } from "react-icons/lu";
import useTitanApp from "../../../../../hooks/useTitanApp";
import { RiWalkFill } from "react-icons/ri";
import { RxCaretDown, RxCaretUp } from "react-icons/rx";
import { motion } from "framer-motion";
import { FiArrowRight } from "react-icons/fi";
import { IoIosCheckmarkCircle, IoIosSearch } from "react-icons/io";
import { CiCirclePlus } from "react-icons/ci";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  setIsDemDXTemplate,
  setOpenAddTemplate,
  setSecondPatientPanelTitle,
  setTogglePatientPanelTwo,
  setCurrentShowEditAiTemplateModal,
  setDemDxSummary,
  setIsDemDxActive,
  setPatientSummary,
  setDemDXForChat,
  setPatientTranscription,
  addConsultationTemplate,
  updateConsultationTemplate,
  setTranscript,
  setDemDxSummaryText,
  setTogglePatientPanelOne,
  setFirstPatientPanelTitle,
  getPatientConsultationByConsultationId,
  getPatientDocumentById,
  setViewOpen,
  setSnomedCodes,
  setIsPatientDashboard,
} from "../../../../../dispatchers/patientDispatchers";
import {
  setViewingDoc,
  getAllDocumentsByEmail,
} from "@/dispatchers/documentDispatchers";
import { Skeleton } from "@mui/material";
import { genderTypes } from "../../../../../enum/genderTypes";
import {
  getAiTemplateById,
  setAiqa,
  setHydratedTemplateSections,
  setIsTemplateSelected,
  setSelectedTemplate,
  setStreamedText,
} from "../../../../../dispatchers/templateDispatchers";
import { FiEdit2 } from "react-icons/fi";
import PatientDemDxIframe from "./PatientDemDxIframe"; // adjust the path as needed
import config from "../../../../../config.js";
import { v4 as uuidv4 } from "uuid";
import { ArrowRight, FileText, PenTool, Plus } from "lucide-react";
import NotesPanel from "../../NotesPanel";
import { ConsultationButtonStatus } from "../../../../../enum/ConsultationButtonStatus.js";
import ErrorConsultationModal from "../../ErrorConsultationModal.tsx";
import {
  setConsultationLoadError,
  setCurrentGlobalTabText,
  setCurrentGlobalTabValue,
  setisPatientSearched,
} from "../../../../../dispatchers/appDispatchers.js";
import { useDocumentsData } from "../../../../Documents/documentsData.ts";
import { NHSServicesForms } from "../../../../../components/nhs-templates/NHSServicesForms.jsx";
import { useToast } from "@/hooks/use-toast";
import {
  nhs111ServiceId,
  nhs111TemplateId,
  nhsContraceptionTemplateId,
  nhsSinusitisTemplateId,
  nhsSoreThroatTemplateId,
  nhsImpetigoTemplateId,
  nhsInsectBitesTemplateId,
  nhsShinglesTemplateId,
  nhsUtiTemplateId,
  nhsAomTemplateId,
  nhsHypertensionStage1TemplateId,
  nhsHypertensionABPMFittingTemplateId,
  nhsHypertensionABPMFollowupTemplateId,
  nhsAhtWeightTemplateId,
  nhsAhtTravelHealthTemplateId,
  nhsAhtWeightServiceId,
  nhsAhtTravelHealthServiceId,
} from "@/data/constantStrings";
import { Badge } from "@/components/ui/badge";
import { checkUserPermissions } from "@/utils/checkUserPermissions";
import {
  accessTitanverseTemplateUpsert,
  accessTitanversePatientView,
  accessTitanverseApprovalView,
  accessTitanverseCalenderView,
  accessTitanverseCalenderUpsert,
} from "@/data/constantStrings";
import { getCompanyDocumentsByCompanyId } from "@/dispatchers/companyDocumentsDispatchers";
import { usePatientVitalsState } from "@/lib/dashboard/vitalsData";
import { getCompanyMarketplaceApps } from "@/dispatchers/marketAppDispacher";
import {
  isNhsService,
  getNhsServiceName,
} from "@/components/nhs-templates/NHSServicesHelper";
import {
  clearTreatmentPlanDraft,
  saveTreatmentPlanDraft,
} from "@/dispatchers/treatmentPlanDispatchers";

export default function PatientTemplate({
  stopTranscription,
  isDemDXGlobalCheckRef,
}) {
  const { dexDxUrl, demDxIssuer, license, demDxToken } = config;
  const {
    appState,
    templateState,
    patientState,
    pharmacyServiceState,
    scheduleEventState,
    treatmentPlanState,
    marketAppState,
  } = useTitanApp();
  const { showNHS111ReferralTab, allPharmacies, defaultPharmacyServices = [] } =
    pharmacyServiceState || {};
  const { scheduleEvents = [] } = scheduleEventState || {};

  const canUpsertTemplate = checkUserPermissions(
    accessTitanverseTemplateUpsert,
  );
  const {
    darkMode,
    consultationLoadError,
    features,
    companyId,
    currentPharmacyId,
    currentPharmacy,
  } = appState || {};
  const navigate = useNavigate();
  const {
    isDropdownAiTemplatesLoading,
    currentAiTemplates,
    isSelectedAiTemplateLoading,
    selectedTemplate,
    aiqaData,
    hydratedTemplateSections,
    aiTemplateLoadError,
  } = templateState || {};
  const {
    demDXTemplates,
    selectedPatientConsultation,
    isConsultationEnabled,
    selectedPatientDetails,
    isDemDXTemplate,
    demDxSummary,
    consultationId,
    isCallStarted,
    consultationMediaButtonMode,
    isSummaryGenerated,
    hasPreConsultTab,
  } = patientState || {};

  const {
    patientActiveTreatmentPlans = [],
    linkedTreatmentPlanId,
    treatmentPlanDrafts = {},
  } = treatmentPlanState || {};

  const hasExistingTreatmentPlanForConsultation = useMemo(() => {
    if (!consultationId) return false;

    const currentTreatmentPlanDraft =
      treatmentPlanDrafts?.[consultationId] ?? null;
    if (currentTreatmentPlanDraft) return true;
    if (linkedTreatmentPlanId) return true;

    return (patientActiveTreatmentPlans || []).some((plan: any) => {
      const planConsultationId = plan?.consultationId || plan?.consultationID;
      const followUps = plan?.followUpAppointments || [];
      return (
        String(planConsultationId || "") === String(consultationId) ||
        followUps.some(
          (apt: any) =>
            String(apt?.consultationId || apt?.consultationID || "") ===
            String(consultationId),
        )
      );
    });
  }, [
    consultationId,
    linkedTreatmentPlanId,
    patientActiveTreatmentPlans,
    treatmentPlanDrafts,
  ]);

  const [templateDropdown, setTemplateDropdown] = useState(false);
  const [
    isAHTWeightManagementInstalled,
    setIsAHTWeightManagementInstalled,
  ] = useState(false);
  const [
    isAHTTravelConsultationInstalled,
    setIsAHTTravelConsultationInstalled,
  ] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [demDxForceReload, setDemDxForceReload] = useState(0);
  // Covers the addConsultationTemplate + getAiTemplateById calls that
  // handleTemplateSelection fires when selecting AHT Weight Management.
  const [isAhtTemplateSelectionLoading, setIsAhtTemplateSelectionLoading] =
    useState(false);
  const { companyDocuments, fetchDocumentById } = useDocumentsData();
  const documents = companyDocuments;


  const [ahtWeightDocuments, setAhtWeightDocuments] = useState<any[]>([]);
  const [userAssignedDocuments, setUserAssignedDocuments] = useState<any[]>(
    [],
  );
  const isAhtWeightConsultation =
    selectedPatientConsultation?.templateId === nhsAhtWeightTemplateId;
  // Load the current user's assigned documents on mount and every time the
  // template dropdown opens, so sign-off status (forceUserSignOff templates
  // and AHT weight documents) reflects documents read since the last check.
  useEffect(() => {
    if (templateDropdown === false && userAssignedDocuments.length > 0) return;

    let cancelled = false;
    (async () => {
      const result = await getAllDocumentsByEmail();
      if (cancelled) return;
      const assignedDocuments = result?.assignedDocuments || [];
      setUserAssignedDocuments(assignedDocuments);
      setAhtWeightDocuments(
        assignedDocuments.filter((doc: any) => doc?.documentType === 13),
      );
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateDropdown, isAhtWeightConsultation]);

  // A template with forceUserSignOff is only blocked until the current user
  // has read / completed its linked document.
  const isTemplateSignOffPending = (template: any) => {
    if (template?.forceUserSignOff !== true) return false;
    if (!template?.linkedDocumentId) return false;
    const linkedDocumentId = String(template.linkedDocumentId).trim();
    const linkedDoc = userAssignedDocuments.find(
      (doc: any) =>
        String(doc?.documentId || doc?._id || "").trim() === linkedDocumentId,
    );
    return !(linkedDoc?.compliant || linkedDoc?.markAsRead);
  };

  const nonCompliantAhtWeightDocuments = useMemo(
    () =>
      ahtWeightDocuments.filter(
        (doc: any) => !(doc?.compliant || doc?.markAsRead),
      ),
    [ahtWeightDocuments],
  );
  const isAhtWeightCompliant =
    ahtWeightDocuments.length > 0 &&
    nonCompliantAhtWeightDocuments.length === 0;
  const [isNhsSelected, setIsNhsSelected] = useState(
    () =>
      isNhsService(selectedPatientConsultation?.templateId) ||
      selectedPatientConsultation?.template?.templateSections?.some(
        (s: any) => s.sectionTitle === "__pathway_engine_metadata__",
      ) === true,
  );

  const {
    ifNHS111ServiceEnabled,
    ifPharmacyFirstEnabled,
    ifAlliedHealthTrainingEnabled,
  } = features;

  const { companyMarketplaceAppsData } = marketAppState || {};

  const normalizeIntervalUnit = (unit: any): "Days" | "Weeks" | "Months" => {
    if (typeof unit === "number") {
      const map: Record<number, "Days" | "Weeks" | "Months"> = {
        1: "Days",
        2: "Weeks",
        3: "Months",
      };
      return map[unit] || "Weeks";
    }

    const normalized = String(unit ?? "")
      .trim()
      .toLowerCase();
    if (["day", "days"].includes(normalized)) return "Days";
    if (["month", "months"].includes(normalized)) return "Months";
    return "Weeks";
  };

  const normalizeRecurrenceFrequency = (
    value: any,
  ): "Weekly" | "Monthly" | "Yearly" => {
    if (typeof value === "number") {
      const map: Record<number, "Weekly" | "Monthly" | "Yearly"> = {
        1: "Weekly",
        2: "Monthly",
        3: "Yearly",
      };
      return map[value] || "Weekly";
    }

    const normalized = String(value ?? "")
      .trim()
      .toLowerCase();
    if (["monthly", "month", "months"].includes(normalized)) return "Monthly";
    if (["yearly", "year", "years"].includes(normalized)) return "Yearly";
    return "Weekly";
  };

  // RecurrenceDurationUnit enum differs from IntervalUnit (Weeks=1, Months=2, Years=3)
  const normalizeRecurrenceDurationUnit = (
    unit: any,
  ): "Weeks" | "Months" | "Years" => {
    if (typeof unit === "number") {
      const map: Record<number, "Weeks" | "Months" | "Years"> = {
        1: "Weeks",
        2: "Months",
        3: "Years",
      };
      return map[unit] || "Months";
    }

    const normalized = String(unit ?? "")
      .trim()
      .toLowerCase();
    if (["week", "weeks"].includes(normalized)) return "Weeks";
    if (["year", "years"].includes(normalized)) return "Years";
    return "Months";
  };

  const RECURRENCE_FREQUENCY_STEP: Record<
    "Weekly" | "Monthly" | "Yearly",
    { amount: number; unit: "Days" | "Weeks" | "Months" }
  > = {
    Weekly: { amount: 1, unit: "Weeks" },
    Monthly: { amount: 1, unit: "Months" },
    Yearly: { amount: 1, unit: "Months" }, // Years -> handled as 12 months below
  };

  const toDateOnlyString = (value: string | Date | undefined): string => {
    if (!value) return "";

    const raw = String(value).trim();
    const dateOnly = raw.match(/^\d{4}-\d{2}-\d{2}/)?.[0];
    if (dateOnly) return dateOnly;

    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toISOString().split("T")[0];
    }

    return raw.split("T")[0]?.split(" ")[0] || "";
  };

  const addIntervalToDate = (
    baseDate: string | Date | undefined,
    intervalValue: string | number | undefined,
    intervalUnit: string | number | undefined,
  ): string => {
    const cleanBaseDate = toDateOnlyString(baseDate);
    if (!cleanBaseDate) return "";

    const parsedValue = Number(intervalValue ?? 0);
    if (!Number.isFinite(parsedValue) || parsedValue <= 0) return "";

    const normalizedUnit = normalizeIntervalUnit(intervalUnit);
    const [year, month, day] = cleanBaseDate.split("-").map(Number);
    if (!year || !month || !day) return "";

    const baseLocalDate = new Date(year, month - 1, day);
    const nextDate = new Date(baseLocalDate);
    if (normalizedUnit === "Days") {
      nextDate.setDate(nextDate.getDate() + parsedValue);
    } else if (normalizedUnit === "Weeks") {
      nextDate.setDate(nextDate.getDate() + parsedValue * 7);
    } else {
      nextDate.setMonth(nextDate.getMonth() + parsedValue);
    }

    return `${nextDate.getFullYear()}-${String(
      nextDate.getMonth() + 1,
    ).padStart(2, "0")}-${String(nextDate.getDate()).padStart(2, "0")}`;
  };

  // RecurrenceDurationValue/Unit describe the TOTAL SPAN to repeat over (e.g.
  // "Weekly for 2 Months"), not the number of occurrences directly — so the
  // occurrence count must be derived by stepping from the start date until
  // the span end date is reached, not by reading the duration value as-is.
  const computeRecurrenceOccurrenceCount = (
    startDate: string,
    stepAmount: number,
    stepUnit: "Days" | "Weeks" | "Months",
    durationValue: string | number | undefined,
    durationUnit: any,
  ): number => {
    if (!startDate) return 1;

    const parsedDurationValue = Number(durationValue ?? 0) || 1;
    const normalizedDurationUnit = normalizeRecurrenceDurationUnit(
      durationUnit,
    );
    const isYears = normalizedDurationUnit === "Years";
    const durationAmount = isYears
      ? parsedDurationValue * 12
      : parsedDurationValue;
    const durationUnitForAdd: "Weeks" | "Months" = isYears
      ? "Months"
      : normalizedDurationUnit === "Weeks"
      ? "Weeks"
      : "Months";

    const endDate = addIntervalToDate(
      startDate,
      durationAmount,
      durationUnitForAdd,
    );
    if (!endDate) return 1;

    let count = 1;
    let currentDate = startDate;
    // safety cap to avoid runaway loops if data is malformed
    for (let i = 0; i < 200; i++) {
      const nextDate = addIntervalToDate(currentDate, stepAmount, stepUnit);
      // exclude the boundary itself, otherwise the span end is counted as an extra occurrence
      if (!nextDate || nextDate >= endDate) break;
      currentDate = nextDate;
      count++;
    }

    return count;
  };

  const getConfiguredFollowUpAppointments = (
    appointments: any[] = [],
    consultationStartDate?: string,
  ) => {
    const now = new Date();
    const consultationEvent = (scheduleEvents || []).find((event: any) => {
      const eventConsultationId =
        event?.ConsultationID || event?.consultationID || event?.EventId;
      return String(eventConsultationId || "") === String(consultationId || "");
    });

    const extractTimeFromDateTime = (value?: string): string => {
      if (!value) return "";
      const match = String(value).match(/(?:T|\s)(\d{2}:\d{2})/);
      return match?.[1] || "";
    };

    const consultationServiceName =
      selectedPatientConsultation?.serviceName || "";
    const consultationServiceId =
      selectedPatientConsultation?.serviceID ||
      selectedPatientConsultation?.serviceId ||
      consultationEvent?.ServiceID ||
      consultationEvent?.serviceID ||
      consultationEvent?.serviceId ||
      "";
    const matchedBookedService = (defaultPharmacyServices || []).find(
      (service: any) =>
        String(service?._id || service?.serviceID || service?.id || "") ===
        String(consultationServiceId || ""),
    );
    const bookedServiceName =
      consultationServiceName ||
      matchedBookedService?.name ||
      matchedBookedService?.serviceName ||
      "";
    const hasAppointmentContext = Boolean(
      selectedPatientConsultation?.engagementDate ||
        consultationEvent?.StartDate ||
        consultationEvent?.startDate,
    );
    const shouldUseBookedServiceForFirstAppointment = Boolean(
      hasAppointmentContext,
    );
    const bookedResourceId =
      consultationEvent?.ResourceId ||
      consultationEvent?.resourceId ||
      selectedPatientConsultation?.resourceId ||
      selectedPatientConsultation?.resourceID ||
      "";
    const bookedPharmacistName =
      consultationEvent?.CalenderName ||
      selectedPatientConsultation?.resourceName ||
      selectedPatientConsultation?.pharmacist ||
      "";

    const baseDate =
      toDateOnlyString(consultationStartDate) ||
      toDateOnlyString(consultationEvent?.StartDate) ||
      now.toISOString().split("T")[0];
    const defaultTime = `${String(now.getHours()).padStart(2, "0")}:${String(
      now.getMinutes(),
    ).padStart(2, "0")}`;
    const bookedFirstAppointmentTime =
      extractTimeFromDateTime(consultationStartDate) ||
      extractTimeFromDateTime(consultationEvent?.StartDate) ||
      selectedPatientConsultation?.time ||
      defaultTime;

    let previousComputedDate = baseDate;

    return appointments.flatMap((a: any, index: number) => {
      const intervalValue = a.intervalValue ?? a.IntervalValue ?? "";
      const intervalUnit = a.intervalUnit ?? a.IntervalUnit ?? "Weeks";
      const configuredServiceName =
        a.serviceType || a.ServiceType || a.serviceName || a.ServiceName || "";
      const isFollowUpIntervalEntry = index > 0;

      // recurrence config for this entry — computed up front now, because
      // entry 0's first occurrence needs it as a fallback offset
      const isRecurring = a.isRecurring ?? a.IsRecurring ?? false;
      const recurrenceDurationValueRaw =
        a.recurrenceDurationValue ?? a.RecurrenceDurationValue;
      const recurrenceDurationUnitRaw =
        a.recurrenceDurationUnit ?? a.RecurrenceDurationUnit ?? "Months";
      const frequency = normalizeRecurrenceFrequency(
        a.recurrenceFrequency ?? a.RecurrenceFrequency,
      );
      const step = RECURRENCE_FREQUENCY_STEP[frequency];
      const stepAmount =
        frequency === "Yearly" ? step.amount * 12 : step.amount;

      // date of this config entry's FIRST occurrence:
      // - entry 2+: chains from the PREVIOUS entry's last occurrence via
      //   IntervalUnit/IntervalValue (unchanged)
      // - entry 1 with an explicit IntervalValue: honor it
      // - entry 1 with no IntervalValue (your data): these are follow-ups,
      //   not same-day appointments, so push out one RecurrenceFrequency
      //   step from baseDate instead of using baseDate as-is
      const entryFirstOccurrenceDate = isFollowUpIntervalEntry
        ? addIntervalToDate(previousComputedDate, intervalValue, intervalUnit)
        : intervalValue
        ? addIntervalToDate(baseDate, intervalValue, intervalUnit)
        : isRecurring
        ? addIntervalToDate(baseDate, stepAmount, step.unit)
        : baseDate;

      // occurrence count is derived from the RecurrenceDuration span (e.g.
      // "Weekly for 2 Months"), stepping from this entry's first occurrence
      const occurrenceCount = isRecurring
        ? computeRecurrenceOccurrenceCount(
            entryFirstOccurrenceDate || baseDate,
            stepAmount,
            step.unit,
            recurrenceDurationValueRaw,
            recurrenceDurationUnitRaw,
          )
        : 1;

      const preSelectedLabel =
        isFollowUpIntervalEntry && (intervalValue || intervalUnit)
          ? `${Number(intervalValue || 0) || ""} ${normalizeIntervalUnit(
              intervalUnit,
            )}`.trim()
          : "";

      const occurrences: any[] = [];
      let occurrenceDate = entryFirstOccurrenceDate;

      for (let occIndex = 0; occIndex < occurrenceCount; occIndex++) {
        if (occIndex > 0) {
          occurrenceDate = addIntervalToDate(
            occurrenceDate,
            stepAmount,
            step.unit,
          );
        }

        const isVeryFirstOverall = index === 0 && occIndex === 0;

        occurrences.push({
          ...a,
          id: occIndex === 0 && a.id ? a.id : uuidv4(),
          name:
            occurrenceCount > 1
              ? `${a.name || a.Name || ""} ${occIndex + 1}`
              : a.name || a.Name || "",
          serviceId: a.serviceId || a.ServiceId || a.ServiceID || "",
          serviceType: isVeryFirstOverall
            ? shouldUseBookedServiceForFirstAppointment
              ? bookedServiceName
              : configuredServiceName
            : configuredServiceName,
          date: occurrenceDate || toDateOnlyString(a.date) || "",
          time: "", //isVeryFirstOverall ? bookedFirstAppointmentTime : a.time ||
          resourceId: isVeryFirstOverall ? bookedResourceId : "",
          pharmacist: isVeryFirstOverall ? bookedPharmacistName : "",
          duration: String(a.duration || "30"),
          roomId: a.roomId || a.RoomId || "",
          room: a.room || "",
          notes: "",
          intervalValue: a.intervalValue || a.IntervalValue || "",
          intervalUnit: a.intervalUnit || a.IntervalUnit || "Weeks",
          preSelectedBySettings:
            isFollowUpIntervalEntry &&
            occIndex === 0 &&
            !!(intervalValue || intervalUnit),
          preSelectedIntervalLabel: occIndex === 0 ? preSelectedLabel : "",
          isDateManuallySet: false,
          preConsultationTemplateId: a.sendPreConsultationForm
            ? a.preConsultationTemplateID || a.preConsultationTemplateId || null
            : null,
          tasksToComplete: (a.tasks || []).map(
            (t: any) => t.text || t.Text || "",
          ),
          vaccinesAdminister: [],
        });
      }

      // next config entry's IntervalUnit/IntervalValue offset chains from
      // THIS entry's LAST occurrence, not its first
      previousComputedDate = occurrenceDate || previousComputedDate;

      return occurrences;
    });
  };

  useEffect(() => {
    if (companyId) {
      getCompanyMarketplaceApps(companyId);
    }
  }, [companyId]);

  const currentPharmacyOds = useMemo(() => {
    const matched = allPharmacies?.find(
      (p: any) => (p?.teamPharmacyID || p?.pharmacyID) === currentPharmacyId,
    );
    return matched?.odsCode || matched?.ODSCode || matched?.ods || "";
  }, [allPharmacies, currentPharmacyId]);

  useEffect(() => {
    const matchedAhtApp = companyMarketplaceAppsData?.find(
      (app: any) =>
        app.marketType === 5 &&
        (app.ods || "").trim().toLowerCase() ===
          (currentPharmacyOds || "").trim().toLowerCase(),
    );

    setIsAHTWeightManagementInstalled(
      !!matchedAhtApp?.enabledServices?.some(
        (service: any) => service.serviceId === nhsAhtWeightServiceId,
      ),
    );
    setIsAHTTravelConsultationInstalled(
      !!matchedAhtApp?.enabledServices?.some(
        (service: any) => service.serviceId === nhsAhtTravelHealthServiceId,
      ),
    );
  }, [companyMarketplaceAppsData, currentPharmacyOds]);

  const isMalePatient =
    genderTypes[selectedPatientDetails?.sex]?.toLowerCase() === "male";

  const nhsTemplates = [
    {
      _id: nhs111TemplateId,
      templateName: "NHS 111 Emergency Supply",
      demdx_Type: null,
    },
    {
      _id: nhsContraceptionTemplateId,
      templateName: "NHS Contraception",
      demdx_Type: null,
    },
    {
      _id: nhsHypertensionStage1TemplateId,
      templateName: "NHS Hypertension - Stage 1: Initial Consultation",
      demdx_Type: null,
    },
    {
      _id: nhsHypertensionABPMFittingTemplateId,
      templateName: "NHS Hypertension - Stage 2: ABPM Fitting",
      demdx_Type: null,
    },
    {
      _id: nhsHypertensionABPMFollowupTemplateId,
      templateName: "NHS Hypertension - Stage 3: ABPM Follow Up",
      demdx_Type: null,
    },
  ];

  const pharmacyFirstTemplates = [
    {
      _id: nhsSinusitisTemplateId,
      templateName: "NHS Pharmacy First Acute Sinusitis",
      demdx_Type: null,
    },
    {
      _id: nhsSoreThroatTemplateId,
      templateName: "NHS Pharmacy First Acute Sore Throat",
      demdx_Type: null,
    },
    {
      _id: nhsImpetigoTemplateId,
      templateName: "NHS Pharmacy First Impetigo",
      demdx_Type: null,
    },
    {
      _id: nhsInsectBitesTemplateId,
      templateName: "NHS Pharmacy First Infected Insect Bites",
      demdx_Type: null,
    },
    {
      _id: nhsShinglesTemplateId,
      templateName: "NHS Pharmacy First Shingles",
      demdx_Type: null,
    },
    {
      _id: nhsUtiTemplateId,
      templateName: "NHS Pharmacy First Uncomplicated UTI",
      demdx_Type: null,
    },
    {
      _id: nhsAomTemplateId,
      templateName: "NHS Pharmacy First Acute Otitis Media",
      demdx_Type: null,
    },
  ];

  const pgdTemplates = [
    ...(ifAlliedHealthTrainingEnabled && isAHTWeightManagementInstalled
      ? [
          {
            _id: nhsAhtWeightTemplateId,
            templateName: "Weight Management (AHT)",
            demdx_Type: null,
          },
        ]
      : []),
    ...(ifAlliedHealthTrainingEnabled && isAHTTravelConsultationInstalled
      ? [
          {
            _id: nhsAhtTravelHealthTemplateId,
            templateName: "Travel Health (AHT)",
            demdx_Type: null,
          },
        ]
      : []),
  ];

  // existing filteredTemplates definition — add NHS group
  const filteredTemplates =
    selectedPatientConsultation?.serviceID === nhs111ServiceId &&
    selectedPatientConsultation.serviceType === 1
      ? []
      : [
          {
            id: 1,
            title: "Personal Templates",
            templates: currentAiTemplates
              ?.filter((template) =>
                template?.templateName
                  ?.toLowerCase()
                  .includes(searchTerm.toLowerCase()),
              )
              ?.sort((a, b) =>
                a?.templateName
                  ?.toLowerCase()
                  .localeCompare(b?.templateName?.toLowerCase()),
              ),
          },
          {
            id: 2,
            title: "DemDx (Presenting Complaint) Templates",
            isDemDX: true,
            withTags: true,
            templates: [
              ...(demDXTemplates?.predictions?.list_Minor_Illness || []),
              ...(demDXTemplates?.predictions?.list_Pharmacy_First || []),
              ...(demDXTemplates?.predictions?.list_Structured || []),
            ]
              .map((item) => ({
                name: item.name,
                demdx_Type: item.demdx_Type,
                tag_Label: item.tag_Label,
              }))
              .filter((item) =>
                item.name?.toLowerCase().includes(searchTerm.toLowerCase()),
              ),
          },
          {
            id: 3,
            title: "DemDx (Direct Condition) Templates",
            isDemDX: true,
            templates: [...(demDXTemplates?.predictions?.list_Endpoints || [])]
              .map((item) => ({
                name: item.name,
                demdx_Type: item.demdx_Type,
                tag_Label: item.tag_Label,
              }))
              .filter((item) =>
                item.name?.toLowerCase().includes(searchTerm.toLowerCase()),
              ),
          },

          ...(ifNHS111ServiceEnabled
            ? [
                {
                  id: 4,
                  title: "NHS",
                  templates: nhsTemplates
                    .filter(
                      (template) =>
                        !isMalePatient ||
                        template._id !== nhsContraceptionTemplateId,
                    )
                    .filter((template) =>
                      (template.templateName || "")
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase()),
                    ),
                },
              ]
            : []),
          ...(ifPharmacyFirstEnabled
            ? [
                {
                  id: 6,
                  title: "Pharmacy First",
                  templates: pharmacyFirstTemplates.filter((template) =>
                    (template.templateName || "")
                      .toLowerCase()
                      .includes(searchTerm.toLowerCase()),
                  ),
                },
              ]
            : []),
          ...(pgdTemplates.length > 0
            ? [
                {
                  id: 5,
                  title: "PGD",
                  templates: pgdTemplates.filter((template) =>
                    (template.templateName || "")
                      .toLowerCase()
                      .includes(searchTerm.toLowerCase()),
                  ),
                },
              ]
            : []),
        ];
  const handleAnswerChange = (sectionIdx, controlIdx, value) => {
    let currentTemplate = hydratedTemplateSections;
    if (!currentTemplate) {
      const templateSectionsToUse =
        selectedTemplate?.templateSections ??
        selectedPatientConsultation?.template?.templateSections;

      if (!templateSectionsToUse) return;

      currentTemplate = {
        templateSections: templateSectionsToUse.map((section) => ({
          ...section,
          userControls: section.userControls?.map((control) => ({
            ...control,
            value: control.value || "",
          })),
        })),
      };
    }

    const updatedSections = currentTemplate.templateSections.map(
      (section, sIdx) => {
        if (sIdx !== sectionIdx) return section;
        return {
          ...section,
          userControls: section.userControls.map((control, cIdx) =>
            cIdx === controlIdx ? { ...control, value } : control,
          ),
        };
      },
    );

    const updatedTemplate = {
      ...currentTemplate,
      templateSections: updatedSections,
    };

    setHydratedTemplateSections(updatedTemplate);

    if (
      consultationMediaButtonMode === "manual" ||
      selectedPatientConsultation?.consultationButtonStatus ===
        ConsultationButtonStatus.Manual
    ) {
      generateTranscription(updatedTemplate);
    }
  };

  function generateTranscription(hydratedTemplateSections) {
    if (
      !hydratedTemplateSections ||
      !Array.isArray(hydratedTemplateSections?.templateSections)
    ) {
      return "";
    }
    let transcription = "";
    hydratedTemplateSections?.templateSections.forEach((section) => {
      if (Array.isArray(section?.userControls)) {
        section?.userControls?.forEach((control) => {
          if (control.label && control.value) {
            transcription += ` ${control.label} ${control.value} `;
          }
        });
      }
    });
    setTranscript(transcription.trim());
    setPatientTranscription(transcription.trim());
    return transcription.trim();
  }

  const hasInitializedRef = useRef(false);
  // tracks consultationId for which we've already fetched the calendar-default template
  const calendarTemplateFetchedRef = useRef<string | null>(null);

  useEffect(() => {
    const activeConsultation = selectedPatientConsultation?.template;
    const templateSectionsToUse =
      selectedTemplate?.templateSections ??
      activeConsultation?.templateSections;

    if (!templateSectionsToUse || !Array.isArray(templateSectionsToUse)) return;

    if (
      templateSectionsToUse.some(
        (s) => s.sectionTitle === "__pathway_engine_metadata__",
      )
    )
      return;

    const isAiqaEmpty =
      !aiqaData ||
      (Array.isArray(aiqaData) && aiqaData.length === 0) ||
      (typeof aiqaData === "object" && Object.keys(aiqaData).length === 0);

    if (hasInitializedRef.current && isAiqaEmpty) {
      return;
    }

    if (isAiqaEmpty && isSummaryGenerated) {
      return;
    }

    let globalIndex = 0;

    const parsedAnswers = aiqaData ? aiqaData : {};

    const hydratedSections = templateSectionsToUse.map((section, sIdx) => {
      const existingSection =
        hydratedTemplateSections?.templateSections?.[sIdx];

      const hydratedControls = section?.userControls?.map((control, index) => {
        const existingControl = existingSection?.userControls?.[index];
        const existingValue = existingControl?.value;

        const answerKey = `a${globalIndex + 1}`;
        const aiAnswer =
          parsedAnswers[answerKey] &&
          parsedAnswers[answerKey] !== "Not specified"
            ? parsedAnswers[answerKey]
            : null;
        const answer = aiAnswer || existingValue || control.value;

        globalIndex++;
        return {
          ...control,
          value: answer,
        };
      });
      return {
        ...section,
        userControls: hydratedControls,
      };
    });

    const fullHydratedTemplate = {
      ...selectedPatientConsultation,
      templateSections: hydratedSections,
    };

    if (isCallStarted) {
      (async () => {
        try {
          await updateConsultationTemplate({
            consultationId,
            payload: hydratedSections,
          });
        } catch (error) {
          console.error("Error updating consultation template:", error);
          setConsultationLoadError(true);
        }
      })();
    }

    setHydratedTemplateSections(fullHydratedTemplate);
    if (selectedTemplate?.template) {
      setSelectedTemplate(selectedTemplate?.template);
    }

    hasInitializedRef.current = true;
  }, [aiqaData]);

  useEffect(() => {
    hasInitializedRef.current = false;
  }, [selectedTemplate?._id, consultationId]);
  const handleAddTemplates = () => {
    setOpenAddTemplate(true);
  };

  let globalIndex = 0;

  const handleSecondPatientPanelCollapsing = () => {
    setTogglePatientPanelTwo(true);
    setSecondPatientPanelTitle("Template");
  };

  const getBaseDateForTreatmentPlan = (): string => {
    if (!selectedPatientConsultation) return new Date().toISOString();

    if (selectedPatientConsultation?.engagementDate) {
      return selectedPatientConsultation.engagementDate;
    }

    const matchedScheduleEvent = (scheduleEvents || []).find((event: any) => {
      const eventConsultationId =
        event?.ConsultationID || event?.consultationID || event?.EventId;

      return String(eventConsultationId || "") === String(consultationId || "");
    });

    if (matchedScheduleEvent?.StartDate) {
      return matchedScheduleEvent.StartDate;
    }

    if (selectedPatientConsultation?.createdOn) {
      return selectedPatientConsultation.createdOn;
    }

    if (selectedPatientConsultation?.createdAt) {
      return selectedPatientConsultation.createdAt;
    }

    return new Date().toISOString();
  };

  useEffect(() => {
    if (isNhsService(selectedPatientConsultation?.templateId)) {
      setIsNhsSelected(true);
      setPatientSummary(null);
      setDemDxSummary(null);
      setStreamedText("");
      setSnomedCodes([]);
      setDemDxSummaryText("");
      setTemplateDropdown(false);
      setIsTemplateSelected(true);
    } else {
      setIsNhsSelected(false);
    }
  }, [selectedPatientConsultation?.templateId]);

  useEffect(() => {
    const templateId = selectedPatientConsultation?.templateId;

    if (
      !templateId ||
      !consultationId ||
      isNhsService(templateId) ||
      calendarTemplateFetchedRef.current === consultationId
    )
      return;

    calendarTemplateFetchedRef.current = consultationId;

    (async () => {
      const templateData: any = await getAiTemplateById(templateId);
      if ((templateData?.treatmentPlan?.appointments?.length ?? 0) > 0) {
        const hasConfig = !!(
          templateData?.treatmentPlan?.TreatmentPlanConfigurationId ||
          templateData?.treatmentPlan?.treatmentPlanConfigurationId
        );
        // Flow 1 (Calendar): Uses appointment date from engagementDate
        // Flow 2 (Dashboard): Falls back to today's date

        const baseDateForTreatmentPlan = getBaseDateForTreatmentPlan();

        saveTreatmentPlanDraft(consultationId, {
          name: "",
          description: "",
          startDate: hasConfig
            ? baseDateForTreatmentPlan.split("T")[0]
            : new Date().toISOString().split("T")[0],
          endDate: "",
          status: "active",
          goals: [],
          pendingGoals: [],
          treatmentPlanConfigurationId:
            templateData?.treatmentPlan?.TreatmentPlanConfigurationId ||
            templateData?.treatmentPlan?.treatmentPlanConfigurationId ||
            "",
          followUpAppointments: hasConfig
            ? getConfiguredFollowUpAppointments(
                templateData.treatmentPlan.appointments,
                baseDateForTreatmentPlan,
              )
            : templateData.treatmentPlan.appointments.map((a: any) => ({
                id: uuidv4(),
                name: a.name || a.Name || "",
                serviceId: a.serviceId || "",
                serviceType: "",
                date: "",
                time: "",
                resourceId: "",
                pharmacist: "",
                duration: String(a.duration || "30"),
                roomId: a.roomId || a.RoomId || "",
                room: a.room || "",
                notes: "",
                isDateManuallySet: true,
                preConsultationTemplateId: a.sendPreConsultationForm
                  ? a.preConsultationTemplateID ||
                    a.preConsultationTemplateId ||
                    null
                  : null,
                tasksToComplete: (a.tasks || []).map(
                  (t: any) => t.text || t.Text || "",
                ),
                vaccinesAdminister: [],
              })),
          pendingAppointments: [],
          linkedPrescriptions: [],
          isDraft: true,
          savedAt: new Date().toISOString(),
        });
      } else {
        clearTreatmentPlanDraft(consultationId);
      }
    })();
  }, [selectedPatientConsultation?.templateId, consultationId, scheduleEvents]);

  // treatment plan draft builder start

  // treatment plan draft builder end

  const handleTemplateSelection = async (e, template, isDemDxGroup) => {
    const isAhtSelection = template?._id === nhsAhtWeightTemplateId;
    if (isAhtSelection) setIsAhtTemplateSelectionLoading(true);
    try {
      setIsNhsSelected(isNhsService(template._id));
      // The weight PGD needs the width, so the patient panel folds away on selection.
      if (isAhtSelection) {
        setTogglePatientPanelOne(true);
        setFirstPatientPanelTitle("Profile");
      }
      e.stopPropagation();
      setPatientSummary(null);
      setDemDxSummary(null);
      setStreamedText("");
      setSnomedCodes([]);
      setDemDxSummaryText("");
      setTemplateDropdown(false);

      const payloadTemplate = isDemDxGroup
        ? { ...template, templateName: template.name }
        : template;

      if (isDemDxGroup) {
        delete payloadTemplate.name;
      }

      await addConsultationTemplate({
        consultationId: consultationId,
        template: payloadTemplate,
      });
      setSelectedTemplate(template);
      setIsTemplateSelected(true);
      setPatientTranscription("");
      setHydratedTemplateSections(null);
      setAiqa([]);

      if (template?.linkedDocumentId && companyId) {
        await getCompanyDocumentsByCompanyId(companyId);
      }

      if (isDemDxGroup) {
        setTogglePatientPanelOne(true);
        setFirstPatientPanelTitle("Profile");
        isDemDXGlobalCheckRef.current = true;
        setIsDemDxActive(true);
        setDemDXForChat(true);

        const patientId = selectedPatientDetails?.patientID;
        const patientBirthdate = selectedPatientDetails?.dob
          ? new Date(selectedPatientDetails.dob).toISOString().split("T")[0]
          : "";
        const patientGender = genderTypes[selectedPatientDetails?.sex];
        const patientName = selectedPatientDetails?.patientName;

        const isDemDxBracketType =
          template.demdx_Type === "minor_illness" ||
          template.demdx_Type === "pharmacy_first" ||
          template.demdx_Type === "structured_pathway";
        const demDxKey = localStorage.getItem("demDexToken");
        const demDxPayload = {
          assessmentId: consultationId || "",
          demdx_name: isDemDxBracketType
            ? `[${
                template.demdx_Type === "minor_illness"
                  ? "Minor Illness"
                  : template.demdx_Type === "pharmacy_first"
                  ? "Pharmacy First"
                  : "Structured Pathway"
              }] ${template.name || ""}`
            : template.name || "",
          demdx_type: template.demdx_Type,
          licenseKey: demDxKey,
          subject: {
            id: patientId,
            birthdate: patientBirthdate,
            gender: patientGender,
            name: patientName,
          },
          token: demDxToken,
        };

        setIsDemDXTemplate(demDxPayload);
        setDemDxForceReload((prev) => prev + 1); // Force reload
      } else {
        setIsDemDxActive(false);
        setIsDemDXTemplate(null);
        if (isConsultationEnabled) return;

        setSearchTerm("");
        setAiqa([]);
        const templateData: any = await getAiTemplateById(template._id);
        if (templateData?.treatmentPlan?.appointments?.length > 0) {
          const hasConfig = !!(
            templateData?.treatmentPlan?.TreatmentPlanConfigurationId ||
            templateData?.treatmentPlan?.treatmentPlanConfigurationId
          );
          // Flow 2 (Dashboard Selection): Uses current date when template is selected from dashboard
          // Flow 1 would use appointment date but this branch is for new template selection in Flow 2
          const baseDateForTreatmentPlan = getBaseDateForTreatmentPlan();
          const treatmentPlanDraft = {
            name: "",
            description: "",
            startDate: hasConfig
              ? baseDateForTreatmentPlan.split("T")[0]
              : new Date().toISOString().split("T")[0],
            endDate: "",
            status: "active",
            goals: [],
            pendingGoals: [],
            treatmentPlanConfigurationId:
              templateData?.treatmentPlan?.TreatmentPlanConfigurationId ||
              templateData?.treatmentPlan?.treatmentPlanConfigurationId ||
              "",
            followUpAppointments: hasConfig
              ? getConfiguredFollowUpAppointments(
                  templateData.treatmentPlan.appointments,
                  baseDateForTreatmentPlan,
                )
              : templateData.treatmentPlan.appointments.map((a: any) => ({
                  id: uuidv4(),
                  name: a.name || a.Name || "",
                  serviceId: a.serviceId || "",
                  serviceType: "",
                  date: "",
                  time: "",
                  resourceId: "",
                  pharmacist: "",
                  duration: String(a.duration || "30"),
                  roomId: a.roomId || a.RoomId || "",
                  room: a.room || "",
                  notes: "",
                  isDateManuallySet: true,
                  preConsultationTemplateId: a.sendPreConsultationForm
                    ? a.preConsultationTemplateID ||
                      a.preConsultationTemplateId ||
                      null
                    : null,
                  tasksToComplete: (a.tasks || []).map(
                    (t: any) => t.text || t.Text || "",
                  ),
                  vaccinesAdminister: [],
                })),
            pendingAppointments: [],
            linkedPrescriptions: [],
            isDraft: true,
            savedAt: new Date().toISOString(),
          };

          saveTreatmentPlanDraft(consultationId, treatmentPlanDraft);
        } else if (hasExistingTreatmentPlanForConsultation) {
          clearTreatmentPlanDraft(consultationId);
        }
      }
      await getPatientConsultationByConsultationId(consultationId);
    } catch (error) {
      console.error("Error in handleTemplateSelection:", error);
      setConsultationLoadError(true);
    } finally {
      if (isAhtSelection) setIsAhtTemplateSelectionLoading(false);
    }
  };

  const templateSectionsToRenderRaw =
    Array.isArray(hydratedTemplateSections?.templateSections) &&
    hydratedTemplateSections.templateSections.length > 0
      ? hydratedTemplateSections.templateSections
      : Array.isArray(
          selectedPatientConsultation?.template?.templateSections,
        ) && selectedPatientConsultation?.template?.templateSections.length > 0
      ? selectedPatientConsultation?.template?.templateSections
      : selectedTemplate?.templateSections &&
        Array.isArray(selectedTemplate?.templateSections) &&
        selectedTemplate?.templateSections.length > 0
      ? selectedTemplate?.templateSections
      : null;

  const isPathwaySavedState = templateSectionsToRenderRaw?.some(
    (s) =>
      s.sectionTitle === "__pathway_engine_metadata__" ||
      s.sectionTitle === "__travel_health_metadata__",
  );
  const templateSectionsToRender = isPathwaySavedState
    ? null
    : templateSectionsToRenderRaw;

  const templateNameToShow = isNhsSelected
    ? getNhsServiceName(selectedPatientConsultation?.templateId) ||
      selectedTemplate?.templateName ||
      selectedPatientConsultation?.template?.templateName ||
      selectedTemplate?.name ||
      "Choose a template"
    : selectedTemplate?.templateName ||
      selectedPatientConsultation?.template?.templateName ||
      selectedTemplate?.name ||
      "Choose a template";

  const isManualMode =
    consultationMediaButtonMode !== "transcription" &&
    (consultationMediaButtonMode === "manual" ||
      selectedPatientConsultation?.consultationButtonStatus ===
        ConsultationButtonStatus.Manual);
  const isTemplateDisabled =
    selectedPatientConsultation?.consultationStatus === 1;

  const linkedDocument =
    selectedTemplate?.linkedDocumentId &&
    documents?.find((doc) => doc._id === selectedTemplate.linkedDocumentId);

  const handleViewDocument = async (doc) => {
    try {
      const documentId = doc?._id || doc?.documentId;

      if (!documentId) {
        console.warn("No document ID found in document data", doc);

        return;
      }

      let result;
      if (doc?._id) {
        result = await fetchDocumentById(
          documentId,
          doc?.mainFile?.fileName,
          1,
        );
      } else {
        result = await getPatientDocumentById(documentId);
      }

      const documentContent = result?.base64Content;
      if (!documentContent) {
        console.warn("No document content received");

        return;
      }

      let fileExtension = result?.fileExtension || result?.fileExtenstion || "";
      const fileName =
        result?.fileName || doc?.mainFile?.fileName || doc?.title || "Document";

      if (fileExtension && !fileExtension.startsWith(".")) {
        fileExtension = "." + fileExtension;
      }

      const mimeMap = {
        ".pdf": "application/pdf",
        ".docx":
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        ".doc": "application/msword",
        ".png": "image/png",
        ".jpg": "image/jpeg",
        ".jpeg": "image/jpeg",
        ".gif": "image/gif",
        ".webp": "image/webp",
        ".txt": "text/plain",
        ".xlsx":
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        ".pptx":
          "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      };

      const mimeType =
        mimeMap[fileExtension?.toLowerCase()] ||
        result?.mimeType ||
        "application/octet-stream";

      // Handle base64 content
      let blobUrl;

      if (documentContent.startsWith("data:")) {
        // Already has data URI prefix, use directly
        blobUrl = documentContent;
      } else {
        // Convert base64 to blob
        const byteCharacters = atob(documentContent);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: mimeType });
        blobUrl = URL.createObjectURL(blob);
      }

      setViewingDoc({
        title: fileName,
        url: blobUrl,
        mimeType,
      });
      setViewOpen(true);
    } catch (err) {
      console.error("Error loading document:", err);
    }
  };
  const showPreConsult = hasPreConsultTab;

  const isTemplateOverdue = (template) => {
    if (!template.lastReviewedAt) return true;

    const lastReviewDate = new Date(template.lastReviewed);
    const currentDate = new Date();

    // Calculate the date 12 months ago from the current date
    const twelveMonthsAgo = new Date(
      currentDate.getFullYear() - 1,
      currentDate.getMonth(),
      currentDate.getDate(),
    );

    // If the last review date is before the date 12 months ago, it's overdue.
    return lastReviewDate < twelveMonthsAgo;
  };

  return (
    <div className="pc-tabbed-content patient-template-wrapper">
      {isManualMode && (
        <div
          role="status"
          aria-live="polite"
          className={`px-4 py-3 border-b mb-4 animate-in fade-in slide-in-from-top-2 duration-200 ${
            darkMode
              ? "bg-amber-900/20 border-amber-700/30"
              : "bg-amber-50 border-amber-200"
          }`}
        >
          <div className="flex items-start gap-3">
            <PenTool
              className={`w-5 h-5 shrink-0 mt-0.5 transition-colors duration-300 ${
                darkMode ? "text-amber-400" : "text-amber-600"
              }`}
            />
            <div className="flex-1 min-w-0">
              <h3
                className={`font-semibold text-sm transition-colors duration-300 ${
                  darkMode ? "text-amber-300" : "text-amber-900"
                }`}
              >
                Manual Entry Mode Active
              </h3>
              <p
                className={`text-xs mt-1 transition-colors duration-300 ${
                  darkMode ? "text-amber-200" : "text-amber-800"
                }`}
              >
                You are recording this consultation manually. When you have
                finished, select{" "}
                <span className="font-semibold">Finish Manual Entry</span> to
                generate the consultation summary.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="section-title">
        <div className="name-collapsible">
          <LuPanelRightOpen
            className="collapse-icon"
            color={darkMode ? "#fff" : "#0a0a0a"}
            onClick={handleSecondPatientPanelCollapsing}
          />
          <h5 className="font-medium">Patient Consultation</h5>
          <Badge variant="secondary" className="text-xs">
            <RiWalkFill className="mr-1" /> Walk in
          </Badge>
        </div>
      </div>

      <motion.div
        className="template-dropdown"
        onClick={(e) => {
          if (isTemplateDisabled) {
            e.stopPropagation();
            return;
          }
          if (
            selectedPatientConsultation?.serviceID === nhs111ServiceId &&
            selectedPatientConsultation.serviceType === 1
          )
            return;
          if (
            e.target.closest(".template-list-box") ||
            e.target.closest(".search-box") ||
            e.target.closest(".template-item")
          ) {
            e.stopPropagation();
            return;
          }

          e.stopPropagation();
          if (
            !isConsultationEnabled &&
            (selectedPatientConsultation?.consultationButtonStatus === 0 ||
              selectedPatientConsultation?.consultationButtonStatus === 4 ||
              selectedPatientConsultation?.consultationStatus !== 2 ||
              selectedPatientConsultation?.consultationButtonStatus ===
                ConsultationButtonStatus.InProgress)
          ) {
            setTemplateDropdown((prev) => !prev);
          }
        }}
        style={{ border: `1px solid ${darkMode ? "#3c3c3c" : "#f3ebeb"}` }}
        animate={{
          maxHeight: templateDropdown
            ? 320
            : isNhsSelected ||
              selectedTemplate?.templateName ||
              selectedTemplate?.name ||
              selectedPatientConsultation?.template?.templateName
            ? 60
            : 42,
        }}
        transition={{ type: "spring", stiffness: 50 }}
      >
        <div className="initial-container">
          <div
            className="header"
            onClick={async (e) => {
              e.stopPropagation();
              if (isTemplateDisabled) return;
              if (
                selectedPatientConsultation?.serviceID === nhs111ServiceId &&
                selectedPatientConsultation.serviceType === 1
              )
                return;

              if (
                !isConsultationEnabled &&
                (selectedPatientConsultation?.consultationButtonStatus === 0 ||
                  selectedPatientConsultation?.consultationButtonStatus === 4 ||
                  selectedPatientConsultation?.consultationStatus !== 2 ||
                  selectedPatientConsultation?.consultationButtonStatus ===
                    ConsultationButtonStatus.InProgress)
              ) {
                setTemplateDropdown((prev) => !prev);
              }
            }}
          >
            <p>Choose a template</p>
            {templateDropdown ? (
              <RxCaretUp size={22} color={darkMode ? "#fff" : "#0a0a0a"} />
            ) : (
              <RxCaretDown size={22} color={darkMode ? "#fff" : "#0a0a0a"} />
            )}
          </div>

          {templateNameToShow !== "Choose a template" && (
            <div className="placeholder-text">
              <p>{templateNameToShow}</p>
              {isAhtWeightConsultation &&
                isAhtWeightCompliant && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 border border-green-200">
                    Compliant
                  </span>
                )}
              {selectedTemplate.demdx_Type && (
                <span
                  className={`demdx-tag ${
                    selectedTemplate.demdx_Type === "minor_illness"
                      ? "tag-minor"
                      : selectedTemplate.demdx_Type === "pharmacy_first"
                      ? "tag-pharmacy"
                      : selectedTemplate.demdx_Type === "structured_pathway"
                      ? "tag-structured"
                      : ""
                  }`}
                >
                  {selectedTemplate.tag_Label != null
                    ? selectedTemplate.tag_Label
                    : selectedTemplate.demdx_Type === "minor_illness"
                    ? "Minor Illness"
                    : selectedTemplate.demdx_Type === "pharmacy_first"
                    ? "Pharmacy First"
                    : selectedTemplate.demdx_Type === "structured_pathway"
                    ? "Minor Illness - Structured"
                    : ""}
                </span>
              )}
            </div>
          )}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{
            opacity: templateDropdown ? 1 : 0,
          }}
          transition={{
            ease: "easeOut",
            duration: 0.3,
            delay: templateDropdown ? 0.3 : 0,
          }}
          className="template-list-box"
        >
          {/* Search Bar */}
          <div className="search-box">
            <IoIosSearch size={14} className="icons-color" />
            <input
              type="text"
              placeholder="Search for a template..."
              className={darkMode ? "text-white" : "text-black"}
              value={searchTerm}
              onChange={(e) => {
                e.stopPropagation();
                setSearchTerm(e.target.value);
              }}
              disabled={isConsultationEnabled || isTemplateDisabled}
            />
          </div>

          {/* Templates List */}
          <div className="template-list">
            {isDropdownAiTemplatesLoading ? (
              Array.from({ length: 2 }).map((_, index) => (
                <div key={index} className="template-item sk">
                  <Skeleton
                    width={250}
                    height={35}
                    animation="wave"
                    sx={{ bgcolor: darkMode ? "grey.900" : "" }}
                  />
                </div>
              ))
            ) : filteredTemplates?.length > 0 ? (
              filteredTemplates.map((group, groupIndex) => (
                <div key={groupIndex} className="template-group">
                  {group?.id === 1 && canUpsertTemplate && (
                    <div
                      className="flex items-center add-new-template-box"
                      onClick={handleAddTemplates}
                    >
                      <Plus className="plus-icon" />
                      <span className="text-blue-medium">
                        Create New Template with AI
                      </span>
                    </div>
                  )}
                  {/* Dynamic Section Title */}
                  <div className="section-title">
                    <p>{group.title}</p>
                  </div>

                  {/* Templates inside the group */}
                  {group.templates?.length > 0 ? (
                    group.templates.map((template, index) => {
                      const isDemDxGroup = group.withTags === true;
                      const tagText = isDemDxGroup
                        ? template.tag_Label !== null
                          ? template.tag_Label
                          : template.demdx_Type === "minor_illness"
                          ? "Minor Illness"
                          : template.demdx_Type === "pharmacy_first"
                          ? "Pharmacy First"
                          : template.demdx_Type === "structured_pathway"
                          ? "Minor Illness - Structured"
                          : null
                        : null;

                      return (
                        <div
                          key={template._id || index}
                          className="template-item"
                          onClick={(e) => {
                            if (isTemplateSignOffPending(template)) {
                              e.stopPropagation();
                              return;
                            }
                            handleTemplateSelection(e, template, group.isDemDX);
                          }}
                        >
                          <FiArrowRight
                            className="arrow"
                            color={darkMode ? "#fff" : "#0a0a0a"}
                          />
                          <p className="flex items-center w-full gap-2 template-name">
                            {template.templateName || template.name}
                            <span className="flex items-center gap-2">
                              {isTemplateSignOffPending(template) && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-red-100 border border-red-200 text-red-800 text-xs font-medium">
                                  Not Compliant
                                </span>
                              )}

                              {group?.id === 1 &&
                                template?.linkedDocumentId &&
                                isTemplateOverdue(template) && (
                                  <Badge
                                    variant="destructive"
                                    className="text-xs px-1.5 py-0.5 bg-red-100 text-red-800 border-red-200"
                                  >
                                    Review Due
                                  </Badge>
                                )}

                              {tagText && (
                                <span
                                  className={`demdx-tag ${
                                    template.demdx_Type === "minor_illness"
                                      ? "tag-minor"
                                      : template.demdx_Type === "pharmacy_first"
                                      ? "tag-pharmacy"
                                      : template.demdx_Type ===
                                        "structured_pathway"
                                      ? "tag-structured"
                                      : ""
                                  }`}
                                >
                                  {tagText}
                                </span>
                              )}
                            </span>

                            {group?.id === 1 && canUpsertTemplate && (
                              <FiEdit2
                                className="ml-auto"
                                color={darkMode ? "#fff" : "#000"}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setCurrentShowEditAiTemplateModal(
                                    true,
                                    template,
                                    templateNameToShow,
                                  );
                                }}
                              />
                            )}
                          </p>
                        </div>
                      );
                    })
                  ) : (
                    <p className="no-templates-dd">
                      No templates found in this group
                    </p>
                  )}
                </div>
              ))
            ) : (
              <p className="no-templates-dd">No templates found</p>
            )}
          </div>
        </motion.div>
      </motion.div>
      {linkedDocument && (
        <div className="mt-2">
          <h4
            className={`text-xs font-medium mb-1 ${
              darkMode ? "text-gray-400" : "text-gray-600"
            }`}
          >
            Linked Document
          </h4>

          <button
            onClick={() => handleViewDocument(linkedDocument)}
            className={`w-full flex items-center gap-1.5 p-2 rounded transition-all text-left ${
              darkMode
                ? "bg-gray-800/50 hover:bg-gray-700/70"
                : "bg-white hover:bg-gray-50 border border-gray-200"
            }`}
          >
            <FileText
              className={`w-3.5 h-3.5 ${
                darkMode ? "text-blue-400" : "text-blue-600"
              }`}
            />

            <div className="flex-1 min-w-0">
              <p
                className={`text-xs font-medium truncate ${
                  darkMode ? "text-gray-200" : "text-gray-800"
                }`}
              >
                {linkedDocument.title}
              </p>
              <p
                className={`text-xs ${
                  darkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                {linkedDocument.type}
              </p>
            </div>

            <ArrowRight
              className={`w-3.5 h-3.5 ${
                darkMode ? "text-gray-500" : "text-gray-800"
              }`}
            />
          </button>
        </div>
      )}

      {isAhtWeightConsultation &&
        ahtWeightDocuments.length > 0 &&
        !isAhtWeightCompliant && (
          <div
            className={`mt-2 rounded border p-3 ${
              darkMode
                ? "border-yellow-800 bg-yellow-950/40"
                : "border-yellow-200 bg-yellow-50"
            }`}
          >
            <h4
              className={`text-xs font-semibold mb-1 ${
                darkMode ? "text-yellow-200" : "text-yellow-900"
              }`}
            >
              Sign-off required before consulting
            </h4>
            <p
              className={`text-xs mb-2 ${
                darkMode ? "text-yellow-300" : "text-yellow-800"
              }`}
            >
              You must read and sign off all required documents before using
              this template.
            </p>
            <div className="space-y-1.5">
              {nonCompliantAhtWeightDocuments.map((doc: any) => (
                <div
                  key={doc.documentId}
                  className="flex items-start gap-3 p-2 rounded-lg border border-yellow-200 bg-yellow-50"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-yellow-900">
                      {doc.title}
                    </p>
                    <p className="text-xs text-yellow-700 mt-0.5">
                      Read and sign off this document in My Documents before
                      using this template.
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      // isPatientSearched gates GlobalTabbedContentView between the
                      // patient-tabbed view (Dashboard/Consultation/PatientList) and
                      // the normal routed pages. Without resetting it, navigate()
                      // changes the URL but the Patient Consultation view (this
                      // screen) keeps rendering on top instead of Documents.
                      setisPatientSearched(false);
                      setIsPatientDashboard(false);
                      // Mirror the tab order in GlobalTabsSwitcher so the
                      // Documents tab is highlighted rather than Patients.
                      let documentsTabIndex = 0;
                      if (features?.ifPharmacyDashboardEnabled) documentsTabIndex++;
                      documentsTabIndex++; // Services
                      if (features?.ifWorkForceTabEnabled) documentsTabIndex++;
                      if (
                        (features?.ifNewCalendar || features?.ifScheduleTabEnabled) &&
                        (checkUserPermissions(accessTitanverseCalenderView) ||
                          checkUserPermissions(accessTitanverseCalenderUpsert))
                      )
                        documentsTabIndex++;
                      if (features?.ifClinicalChecksEnabled) documentsTabIndex++;
                      if (
                        features?.ifKanbanSearchBoxEnabled &&
                        checkUserPermissions(accessTitanversePatientView)
                      )
                        documentsTabIndex++;
                      if (
                        features?.ifKanbanSearchBoxEnabled &&
                        checkUserPermissions(accessTitanverseApprovalView)
                      )
                        documentsTabIndex++;
                      setCurrentGlobalTabValue(documentsTabIndex);
                      setCurrentGlobalTabText("Documents");
                      navigate(
                        `/pharmacy/documents/${currentPharmacy?.teamPharmacyID}?tab=my`,
                      );
                    }}
                    className="shrink-0 text-xs font-medium text-yellow-800 underline underline-offset-2 hover:text-yellow-900 transition-colors whitespace-nowrap"
                  >
                    Go to My Documents →
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

      {isDemDXTemplate ? (
        <div className="iframe-container">
          {isDemDXTemplate && (
            <PatientDemDxIframe
              key={`demdx-${demDxForceReload}`}
              isDemDXTemplate={isDemDXTemplate}
              stopTranscription={stopTranscription}
              ConsultationId={consultationId}
            />
          )}
        </div>
      ) : (
        <div className="template-list-content">
          {isSelectedAiTemplateLoading || isAhtTemplateSelectionLoading ? (
            Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="template-content-list-item sk">
                <Skeleton
                  width={150}
                  height={35}
                  animation="wave"
                  sx={{ bgcolor: darkMode ? "grey.900" : "" }}
                />
                <Skeleton
                  width={400}
                  height={35}
                  animation="wave"
                  sx={{ bgcolor: darkMode ? "grey.900" : "" }}
                />
                <Skeleton
                  width={400}
                  height={35}
                  animation="wave"
                  sx={{ bgcolor: darkMode ? "grey.900" : "" }}
                />
              </div>
            ))
          ) : templateSectionsToRender && !isNhsSelected ? (
            templateSectionsToRender?.map((templateSection, sectionIdx) => (
              <div
                className="template-content-list-item"
                key={templateSection?.groupId || sectionIdx}
              >
                <Accordion className="accordion" defaultExpanded>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    className="header"
                  >
                    <div className="idx">
                      <p>{sectionIdx + 1}</p>
                    </div>
                    <p className="title">
                      {templateSection?.sectionTitle || "-"}
                    </p>
                  </AccordionSummary>
                  <AccordionDetails>
                    <div className="questions-list">
                      {Array.isArray(templateSection?.userControls) &&
                        templateSection?.userControls?.map(
                          (controls, indexInSection) => {
                            const answerKey = `a${globalIndex + 1}`;
                            const currentAnswerObject =
                              aiqaData?.[answerKey] || null;

                            const isAiqaEmpty = aiqaData?.length === 0;

                            const answerValue =
                              isAiqaEmpty && isSummaryGenerated
                                ? controls.value
                                : null;
                            const validAnswer = currentAnswerObject
                              ? Object.entries(
                                  currentAnswerObject,
                                ).find(([key]) => key.startsWith("a"))?.[1]
                              : answerValue;
                            let checkValidQuestion;
                            if (selectedPatientConsultation === null) {
                              checkValidQuestion = validAnswer !== null;
                            } else {
                              const valueLower = controls?.value?.toLowerCase();
                              checkValidQuestion =
                                !controls?.value ||
                                valueLower ===
                                  "not mentioned in the transcript" ||
                                valueLower === "not specified" ||
                                valueLower === "not mentioned" ||
                                valueLower === "not found" ||
                                valueLower === "not found in transcript" ||
                                controls?.value === null
                                  ? false
                                  : true;
                            }
                            const renderedItem =
                              checkValidQuestion && !isManualMode ? (
                                <>
                                  <div
                                    key={globalIndex}
                                    className="question-item answered"
                                  >
                                    <Accordion
                                      className="accordion"
                                      defaultExpanded
                                    >
                                      <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        className="title"
                                      >
                                        <p key={indexInSection}>
                                          {controls?.label || "-"}
                                        </p>
                                        <IoIosCheckmarkCircle
                                          color="#00cc6a"
                                          size={18}
                                        />
                                      </AccordionSummary>
                                      <AccordionDetails>
                                        <p key={indexInSection}>
                                          {controls?.value}
                                        </p>
                                      </AccordionDetails>
                                    </Accordion>
                                  </div>
                                </>
                              ) : (
                                <>
                                  <div
                                    key={globalIndex}
                                    className="question-item not-answered"
                                  >
                                    <p>{controls?.label || "-"}</p>
                                  </div>

                                  {/*Always show input if manual mode */}
                                  {isManualMode && (
                                    <div className="dropdown-item">
                                      <input
                                        type="text"
                                        className="pc-template-answers"
                                        placeholder="Enter your answer..."
                                        required={controls?.required}
                                        value={controls?.value || ""}
                                        onBlur={() => {
                                          setTimeout(() => {
                                            if (
                                              hydratedTemplateSections?.templateSections
                                            ) {
                                              updateConsultationTemplate({
                                                consultationId,
                                                payload:
                                                  hydratedTemplateSections.templateSections,
                                              });
                                            }
                                          }, 200);
                                        }}
                                        onChange={(e) =>
                                          handleAnswerChange(
                                            sectionIdx,
                                            indexInSection,
                                            e.target.value,
                                          )
                                        }
                                      />
                                    </div>
                                  )}
                                </>
                              );

                            globalIndex += 1;
                            return renderedItem;
                          },
                        )}
                    </div>
                  </AccordionDetails>
                </Accordion>
              </div>
            ))
          ) : isNhsSelected || isPathwaySavedState ? (
            <NHSServicesForms
              key={consultationId}
              isDarkMode={darkMode}
              selectedPatientConsultation={selectedPatientConsultation}
              consultationId={consultationId}
              showPreConsult={showPreConsult}
              showNHS111ReferralTab={showNHS111ReferralTab}
              onTemplateCancel={() => setIsNhsSelected(false)}
            />
          ) : (
            <div className="no-templates">
              <p>No Templates found</p>
              <p>Please select a template to continue...</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
